import React from 'react';
import {View, StyleSheet, ViewStyle, TextStyle, TouchableOpacity, Text, StyleProp} from 'react-native';
import {LoaderNeutralLighter, Sizes, TextStyles, Pill, disabledOpacity, onPressOpacity, Colors} from '../index';
import {ComponentProps} from './ComponentProps';

interface ButtonProps extends ComponentProps {
  text: string;
  onPress: () => void;
  // bordered button if false. (by default: true)
  isFilled?: boolean;
  // grays out button if false. (by default: true)
  isEnabled?: boolean;
  // button width fits label width if true or takes all available width if false. default: true
  widthFitsLabel?: boolean;
  iconLeft?: JSX.Element;
  iconRight?: JSX.Element;
  // shows a loading icon. default: false
  isLoading?: boolean;
  // limits space on the right of the button when isLoading is true. Useful when we know the button is small. default: false
  isLoadingCondensed?: boolean;
  height?: number;
}

export const ButtonDefault = (props: ButtonProps) => {
  return <Button {...{...props, color: Colors.primary}} />;
};

export const ButtonWarning = (props: ButtonProps) => {
  return <Button {...{...props, color: Colors.warning}} />;
};

export const ButtonDanger = (props: ButtonProps) => {
  return <Button {...{...props, color: Colors.danger}} />;
};

export const ButtonBrandBase = (props: ButtonProps) => {
  return <Button {...{...props, color: Colors.brandBase}} />;
};

export const ButtonBrandWhite = (props: ButtonProps) => {
  return (
    <Button
      {...{
        ...props,
        color: Colors.brandWhite,
        borderColor: Colors.brandWhite,
        fontColor: Colors.brandBase,
        isNotFilledFontColor: Colors.brandWhite,
      }}
    />
  );
};

interface ButtonPrivateProps extends ButtonProps {
  color: string;
  borderColor?: string;
  fontColor?: string;
  isNotFilledFontColor?: string;
}

const Button = (props: ButtonPrivateProps) => {
  const isFilled = props.isFilled ?? true;
  let isEnabled = props.isEnabled ?? true;
  const widthFitsLabel = props.widthFitsLabel ?? true;
  const isLoading = props.isLoading ?? false;
  const isLoadingCondensed = props.isLoadingCondensed ?? false;
  let iconLeft = props.iconLeft;
  let iconRight = props.iconRight;
  if (isLoading) {
    isEnabled = false;
    iconLeft = <LoaderNeutralLighter />;
    if (!isLoadingCondensed) {
      // to avoid text to move when loading icon appears: put same element on the right
      iconRight = <LoaderNeutralLighter containerStyle={styles.hidden} />;
    }
  }
  let fontColor = props.fontColor ?? Colors.brandWhite;
  if (!isFilled) {
    fontColor = props.isNotFilledFontColor ?? props.color;
  }
  const containerStyle: StyleProp<ViewStyle> = [props.containerStyle ?? {}, {opacity: isEnabled ? 1 : disabledOpacity}];
  const textStyle: StyleProp<TextStyle> = [
    styles.text,
    TextStyles.textMediumMBold,
    {color: fontColor, flex: widthFitsLabel ? 0 : 1},
  ];
  let borderColor = isFilled ? undefined : props.color;
  return (
    <View style={containerStyle} testID={props.testID + '.Container'}>
      <View style={widthFitsLabel ? styles.widthFitsLabel : undefined}>
        <TouchableOpacity
          activeOpacity={isEnabled ? onPressOpacity : 1}
          onPress={isEnabled ? props.onPress : undefined}
          testID={props.testID}>
          <Pill color={isFilled ? props.color : undefined} borderColor={borderColor} height={props.height}>
            <View style={styles.pillContainer}>
              {iconLeft && <View style={styles.iconLeft}>{iconLeft}</View>}
              <Text style={textStyle}>{props.text}</Text>
              {iconRight && <View style={styles.iconRight}>{iconRight}</View>}
            </View>
          </Pill>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  widthFitsLabel: {
    alignItems: 'flex-start',
  },
  fullWidth: {
    flex: 1,
  },
  pillContainer: {
    flexDirection: 'row',
  },
  text: {
    textAlign: 'center',
  },
  iconLeft: {
    marginRight: Sizes.s,
    flexDirection: 'row',
    alignItems: 'center',
  },
  iconRight: {
    marginLeft: Sizes.s,
    flexDirection: 'row',
    alignItems: 'center',
  },
  hidden: {
    opacity: 0,
  },
});
